import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useRef, useCallback, useContext } from 'react';
import useStyles from './AzureMap.styles';
import { SiteContext } from '../../utils/common';
import clsx from 'clsx';
let azureMapsScriptLoaded = null;
const loadAzureMapsScript = (callback) => {
    if (!azureMapsScriptLoaded) {
        azureMapsScriptLoaded = new Promise((resolve) => {
            const scriptTag = document.createElement('script');
            scriptTag.setAttribute('type', 'text/javascript');
            scriptTag.setAttribute('src', `https://atlas.microsoft.com/sdk/javascript/mapcontrol/2/atlas.min.js`);
            scriptTag.async = true;
            scriptTag.defer = true;
            scriptTag.id = 'azureMapsScript';
            document.body.appendChild(scriptTag);
            scriptTag.onload = () => {
                resolve();
                callback();
            };
        });
    }
    else {
        // If script is already loaded, just execute the callback
        azureMapsScriptLoaded.then(callback);
    }
};
const AzureMap = React.forwardRef((props, ref) => {
    const { coordinates, zoomLevel, isFromCnCMap, setOpenIndex, openIndex } = props;
    const { classes } = useStyles();
    const { shorthand } = useContext(SiteContext);
    const mapKey = shorthand === 'ie' ? process.env.NEXT_PUBLIC_AZURE_MAPS_KEY_IE : process.env.NEXT_PUBLIC_AZURE_MAPS_KEY_UK;
    const clientId = shorthand === 'ie'
        ? process.env.NEXT_PUBLIC_AZURE_MAPS_CLIENT_ID_IE
        : process.env.NEXT_PUBLIC_AZURE_MAPS_CLIENT_ID_UK;
    // Refs for map container and map instance
    const mapContainer = useRef(null);
    const map = useRef(null);
    // Create the map with markers and custom options
    const makeMap = useCallback(() => {
        // Initialize map only once
        if (!map.current) {
            map.current = new window.atlas.Map(mapContainer.current, {
                center: [Number(coordinates[0].longitude), Number(coordinates[0].latitude)],
                zoom: zoomLevel,
                authOptions: {
                    authType: 'subscriptionKey',
                    subscriptionKey: mapKey,
                    clientId: clientId,
                },
            });
            map.current.events.add('ready', () => {
                addMarkers(coordinates);
            });
        }
        else {
            // If map already exists, just update markers
            addMarkers(coordinates);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, coordinates, mapKey, zoomLevel]);
    // Separate function to add/update markers
    const addMarkers = (coordinates) => {
        if (!map.current)
            return;
        const pushPinCoordinates = coordinates.map((coordinate) => ({
            position: [Number(coordinate.longitude), Number(coordinate.latitude)],
            type: coordinate.type,
            index: coordinate.index,
        }));
        map.current.markers.clear(); // Remove old markers
        pushPinCoordinates.forEach((pushPin) => {
            const getPushPinContent = pushPin.type
                ? `<div class=${classes.withText}>${pushPin.type}</div>`
                : `<div class=${classes.wihtoutText}></div>`;
            const marker = new window.atlas.HtmlMarker({
                htmlContent: isFromCnCMap && pushPin.index === openIndex
                    ? `<div class=${classes.withSelectedText}></div>`
                    : getPushPinContent,
                position: pushPin.position,
                pixelOffset: [0, -18],
                anchor: 'center',
                color: 'black',
            });
            map.current.markers.add(marker);
            if (isFromCnCMap) {
                // Add click event listener for each pin
                map.current.events.add('click', marker, () => {
                    var _a;
                    setOpenIndex === null || setOpenIndex === void 0 ? void 0 : setOpenIndex((_a = pushPin === null || pushPin === void 0 ? void 0 : pushPin.index) !== null && _a !== void 0 ? _a : 0);
                });
            }
        });
    };
    useEffect(() => {
        // Check if the code is running in the browser
        if (typeof window !== 'undefined' && mapContainer.current) {
            // Load the Azure Maps script and initialize the map
            loadAzureMapsScript(makeMap);
        }
    }, [makeMap]);
    useEffect(() => {
        // Load the Azure Maps CSS
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://atlas.microsoft.com/sdk/javascript/mapcontrol/2/atlas.min.css';
        link.type = 'text/css';
        document.head.appendChild(link);
    }, []);
    return _jsx("div", { ref: ref || mapContainer, className: clsx(classes.azureMap, isFromCnCMap && classes.fullHeight) });
});
AzureMap.displayName = 'AzureMap';
export default AzureMap;
